/*
 * Copyright (c) 2025 CalAmp Corp.  All Rights Reserved
 */
import {useCallback} from 'react';
import {useMutation, useQueryClient} from 'react-query';
import {error} from '@ion/core/src/logger/logger';
import {getJob, removeDevice} from '@ion/core/src/services/Connect';
import {entityId} from '@ion/core/src/services/ConnectUtils';
import {CreateJob, Device, DownloadJobType} from '@ion/core/src/types/entities';
import {QueryKey} from '../config/QueryKeys';
import {createMutationResult} from '../utils/helpers';
import {UseDefaultQueryResult} from './types';

export type UseCancelTaskDeviceResult = UseDefaultQueryResult & {
  cancelTaskDevice: (job: CreateJob, device: Device, index: number) => void;
}

const api = async({job, device, index}) => {
  try {
    const data             = await getJob(job?.jobId);
    const accountId        = entityId(data?.account?.href);
    const {task}           = data?.tasks?.[index];
    const isMDTDevice      = device?.device?.category === 'MDT';
    const deviceIdentifier = isMDTDevice ? 'serialNumber' : 'esn';
    const identifier       = isMDTDevice ? device?.device?.serialNumber : device?.esn || device?.identifier;
    task.progressStatus    = DownloadJobType.Cancelling;
    task.criteria = [
      {
        memberSearch : {
          searchTerms : {
            account            : accountId.toString(),
            [deviceIdentifier] : identifier
          },
          hierLevel : 'ALL'
        }
      }
    ];
    const result = await removeDevice(data);
    return result;
  } catch (err) {
    error(err);
    throw new Error(`An ${err.status || ''} Error Occurred`);
  }
};

/**
 * Common hook for cancelling a Device level Task in Job entity.
 *
 * ```typescript
 *
 *    import useCancelTaskDevice from '@ion/query/src/hooks/';
 *
 *    const {resendShipment, isSuccess, isError} = useCancelTaskDevice();
 *
 * ```
 */
export const useCancelTaskDevice = (): UseCancelTaskDeviceResult => {
  const queryClient = useQueryClient();
  const mutation    = useMutation(api, {
    onSettled : () => {
      queryClient.invalidateQueries([QueryKey.CANCEL_TASK_DEVICE]);
    }
  });

  const cancelTaskDevice = useCallback((job: CreateJob, device: Device, index: number) => {
    mutation.mutate({job, device, index});
  }, [mutation]);

  return createMutationResult(
    mutation, {cancelTaskDevice}
  ) as UseCancelTaskDeviceResult;
};

export default useCancelTaskDevice;
